import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useProducts, useCategories, money, imgUrl } from "./useProducts";
import { PreOrderPanel } from "./PreOrderPanel";
import { DropChip } from "./DropCountdown";
import { StockMeter } from "./StockMeter";
import { isPalestine, showsSpecialEdition, SpecialEditionPill } from "./PalestineCauseBanner";
import { Reviews } from "./Reviews";
import { SuggestedProducts } from "./SuggestedProducts";
import { Stars } from "./Stars";
import { useReviewSummary } from "./useReviews";
import { useCart } from "./cart/CartContext";

const Card = ({ p, index, onOpen, rating }) => {
  const first = p.options[0];
  const separates = p.options.length > 1;
  return (
    <motion.button
      layout
      data-testid={`product-card-${p.slug}`}
      onClick={() => onOpen(p)}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
      className="group relative overflow-hidden rounded-2xl border border-[#e3e2dc] bg-[#faf9f6] text-left flex flex-col hover:border-[#ff2a85]/50 transition-colors duration-500"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[#f3f2ec]">
        <img
          src={imgUrl(p.image)}
          alt={p.name}
          className={`absolute inset-0 w-full h-full object-cover ${p.position} group-hover:scale-[1.04] transition-transform duration-700 ease-out`}
        />

        <div className="absolute top-4 left-4 right-4 z-10 flex flex-wrap items-start justify-between gap-2">
          <span className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#ff2a85] bg-[#ffffff]/80 backdrop-blur px-3 py-1.5 rounded-full border border-[#ff2a85]/30 whitespace-nowrap">
            {p.edition}
          </span>
          {p.remaining === 0 && (
            <span data-testid={`soldout-${p.slug}`} className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-white bg-[#ff2a85] px-3 py-1.5 rounded-full">Sold Out</span>
          )}
          {p.remaining !== 0 && showsSpecialEdition(p) && <SpecialEditionPill slug={p.slug} />}
          {p.remaining !== 0 && (p.badge || p.featured) && (
            <span data-testid={`badge-${p.slug}`} className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-white bg-[#0a0a0b] px-3 py-1.5 rounded-full whitespace-nowrap">
              {p.badge || "Featured"}
            </span>
          )}
        </div>

        <div className="absolute bottom-4 right-4 z-10 h-9 w-9 rounded-full bg-[#ff2a85] flex items-center justify-center opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-400">
          <Plus size={16} className="text-white" />
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display font-extrabold uppercase text-lg sm:text-xl leading-tight">{p.name}</h3>
          <div className="text-right shrink-0">
            <span className="font-mono-d text-sm text-[#0a0a0b] block">
              {money(first.price)}
              {first.regular && <span className="ml-1.5 text-[10px] line-through text-[#5f5e5a]">{money(first.regular)}</span>}
            </span>
            {p.earlyBeliever ? (
              <span className="font-mono-d text-[9px] uppercase tracking-[0.12em] text-[#ff2a85]">Early Believer</span>
            ) : separates && (
              <span className="font-mono-d text-[9px] uppercase tracking-[0.12em] text-[#ff2a85]">{p.category === "accessories" ? `from ${money(Math.min(...p.options.map((o) => o.price)))}` : "or separates"}</span>
            )}
          </div>
        </div>
        {rating && (
          <div data-testid={`card-rating-${p.slug}`} className="mt-1.5 flex items-center gap-1.5">
            <Stars value={rating.avg} size={11} testId={`card-stars-${p.slug}`} />
            <span className="font-mono-d text-[9px] uppercase tracking-[0.12em] text-[#5f5e5a]">{rating.avg.toFixed(1)} · {rating.count} review{rating.count > 1 ? "s" : ""}</span>
          </div>
        )}
        {isPalestine(p) && (
          <p data-testid={`gives-back-${p.slug}`} className="mt-2 inline-flex items-center gap-1.5 font-mono-d text-[9px] uppercase tracking-[0.15em] text-white bg-[#0a0a0b] px-2.5 py-1 rounded-full">
            <span className="h-1.5 w-1.5 rounded-full bg-[#ff2a85]" /> Portion of profit → Palestine cause
          </p>
        )}
        <StockMeter product={p} compact testId={`stock-${p.slug}`} />
        {p.colors?.length > 0 && (
          <div className="mt-3 flex items-center gap-1.5" data-testid={`card-colors-${p.slug}`}>
            {p.colors.map((c) => <span key={c.id} className="h-3.5 w-3.5 rounded-full border border-[#0a0a0b]/20" style={{ backgroundColor: c.hex }} />)}
            <span className="ml-1 font-mono-d text-[9px] uppercase tracking-[0.12em] text-[#5f5e5a]">{p.colors.length} colours</span>
          </div>
        )}
      </div>
    </motion.button>
  );
};

export const Lookbook = ({ initialCategory = "all" }) => {
  const PRODUCTS = useProducts();
  const ratings = useReviewSummary();
  const CATEGORIES = [{ id: "all", label: "All Pieces" }, ...useCategories()];
  const [params] = useSearchParams();
  const [active, setActive] = useState(null);
  const { open: cartOpen } = useCart();
  useEffect(() => {
    if (cartOpen) setActive(null);
  }, [cartOpen]);
  const [color, setColor] = useState(null);
  const [view, setView] = useState(0);
  const [cat, setCat] = useState(initialCategory);
  const visible = cat === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.category === cat);
  const open = (p) => {
    setColor(p.colors?.length ? p.colors[0] : null);
    setView(0);
    setActive(p);
    document.querySelector('[data-testid="product-quickview-modal"]')?.scrollTo({ top: 0, behavior: "smooth" });
  };
  const pickColor = (c) => {
    setColor(c);
    setView(0);
  };
  const images = [...(color?.images?.length ? color.images : [active?.image]), ...(active?.gallery || [])].filter(Boolean).map(imgUrl);

  useEffect(() => {
    const onOpen = (e) => {
      const p = PRODUCTS.find((x) => x.slug === e.detail || x.id === e.detail);
      if (!p) return;
      setCat(p.category);
      document.querySelector('[data-testid="product-showcase-section"]')?.scrollIntoView({ behavior: "smooth" });
      setTimeout(() => open(p), 600);
    };
    window.addEventListener("dd:open-product", onOpen);
    return () => window.removeEventListener("dd:open-product", onOpen);
  }, [PRODUCTS]);

  useEffect(() => {
    const slug = params.get("open");
    if (!slug || !PRODUCTS.length) return;
    const p = PRODUCTS.find((x) => x.slug === slug);
    if (p) setTimeout(() => open(p), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PRODUCTS]);

  return (
    <section data-testid="product-showcase-section" className="relative py-24 sm:py-32 bg-[#f3f2ec]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-[#ff2a85] mb-4 flex items-center gap-3">
              <span className="diamond" /> The Lookbook
            </p>
            <h2 className="font-display font-extrabold uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              Drop 001 <span className="text-stroke">Pieces</span>
            </h2>
          </div>
          <p className="max-w-sm text-[#5f5e5a] text-sm sm:text-base leading-relaxed">
            Hoodie sets can be pre-ordered as a full set or as
            hoodie and pants separately. Prices in USD.
          </p>
        </div>

        <div data-testid="category-filter" className="flex gap-2 overflow-x-auto pb-3 -mx-5 px-5 sm:mx-0 sm:px-0 sm:flex-wrap mb-8 scrollbar-none">
          {CATEGORIES.map((c) => {
            const on = c.id === cat;
            return (
              <button
                key={c.id}
                data-testid={`category-${c.id}`}
                onClick={() => setCat(c.id)}
                className={`shrink-0 h-10 px-5 rounded-full border font-mono-d text-[10px] sm:text-[11px] uppercase tracking-[0.18em] transition-all duration-300 ${
                  on ? "bg-[#0a0a0b] border-[#0a0a0b] text-white" : "bg-[#ffffff] border-[#e3e2dc] text-[#0a0a0b] hover:border-[#ff2a85]"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <Card key={p.id} p={p} index={i} onOpen={open} rating={ratings[p.id]} />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <Dialog open={Boolean(active)} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="product-quickview-modal"
          data-lenis-prevent
          onWheel={(e) => e.stopPropagation()}
          className="max-w-3xl w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto overscroll-contain bg-[#faf9f6] border-[#e3e2dc] text-[#0a0a0b] p-0 gap-0 rounded-2xl [&>button]:hidden"
        >
          {active && (
            <div className="grid sm:grid-cols-2 sm:items-start">
              <div className="relative aspect-[4/3] sm:aspect-auto sm:sticky sm:top-0 sm:h-[90vh] bg-[#f3f2ec]">
                <img
                  key={`${color?.id || "default"}-${view}`}
                  data-testid="quickview-image"
                  src={images[view] || images[0]}
                  alt={`${active.name}${color ? ` — ${color.label}` : ""}`}
                  className={`absolute inset-0 w-full h-full object-cover ${active.position}`}
                />
                {images.length > 1 && (
                  <div data-testid="view-toggle" className="absolute bottom-4 left-4 right-4 flex gap-2 overflow-x-auto scrollbar-none">
                    {images.map((src, i) => (
                      <button
                        key={src}
                        type="button"
                        data-testid={`view-${i === 0 ? "back" : i === 1 ? "front" : i}`}
                        onClick={() => setView(i)}
                        className={`h-14 w-11 shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${view === i ? "border-[#ff2a85]" : "border-white/70 hover:border-white"}`}
                      >
                        <img src={src} alt="" className="h-full w-full object-cover object-top" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-7 sm:p-9 flex flex-col">
                <button
                  data-testid="quickview-close"
                  onClick={() => setActive(null)}
                  className="self-end text-[#5f5e5a] hover:text-[#ff2a85] transition-colors -mt-2 -mr-2"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#ff2a85]">{active.edition}</span>
                  {showsSpecialEdition(active) && <SpecialEditionPill slug={`qv-${active.slug}`} small />}
                  {active.badge && <span className="font-mono-d text-[9px] uppercase tracking-[0.2em] text-white bg-[#0a0a0b] px-2.5 py-1 rounded-full">{active.badge}</span>}
                  <DropChip />
                </div>
                <DialogTitle className="font-display font-extrabold uppercase text-2xl sm:text-3xl leading-tight mt-3 break-words">{active.name}</DialogTitle>
                {active.description && (
                  <p data-testid="product-description" className="mt-4 text-sm leading-relaxed text-[#5f5e5a]">{active.description}</p>
                )}

                <div className="mt-6 space-y-3 text-sm">
                  <Row label="Availability" value={active.edition} />
                </div>

                <PreOrderPanel key={active.id} product={active} color={color} onColorChange={pickColor} />
                <SuggestedProducts key={`suggest-${active.id}`} sources={active} onPick={(p) => open(p)} />
                <Reviews key={`reviews-${active.id}`} product={active} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-[#e3e2dc] pb-3">
    <span className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#5f5e5a] shrink-0">{label}</span>
    <span className="text-[#0a0a0b] text-right">{value}</span>
  </div>
);
